import { axiosInstance } from "../axios-Instance";
import { toast } from "react-toastify";
import { toastOptions } from "../utils";
import { useContext } from "react";
import { AuthContext } from "../context";
import { getLoginToken, setLoginToken } from "../storage";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { LoginProps } from "../interface";
import { queryKeys } from "../react-query/constants";

const SERVER_ERROR = "There was an error contacting the server.";

async function userLogin(formData: LoginProps) {
  const data = await axiosInstance({
    url: "/auth",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}

async function thirdPartyLogin(formData: any) {
  const data = await axiosInstance({
    url: "/auth/thirdParty",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}
async function verifyOtp(formData: any) {
  const data = await axiosInstance({
    url: "/auth/thirdParty/verify",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}

async function extendSession(formData: any) {
  const data = await axiosInstance({
    url: "/auth/extendSession",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

async function forgotPassword(formData: any) {
  try {
    const data = await axiosInstance({
      url: "/auth/forgotPassword",
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

async function resetPassword(formData: any) {
  try {
    const data = await axiosInstance({
      url: `/auth/resetPassword`,
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return data?.data;
  } catch (error) {
    console.error("Error occurred during user registration:", error);
    throw error;
  }
}

export function useLogin() {
  const authCtx = useContext(AuthContext);

  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => userLogin(formData as any),
    onSuccess: (data) => {
      console.log("data=>", data);
      setLoginToken(data.token);
      authCtx.authenticate(data.token);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      // toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useThirdPartyLogin() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => thirdPartyLogin(formData as any),
    onSuccess: (data) => {},
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useExtendSession() {
  const authCtx = useContext(AuthContext);

  const queryClient = useQueryClient();
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => extendSession(formData as any),
    onSuccess: (data) => {
      setLoginToken(data.token);
      authCtx.authenticate(data.token);
      queryClient.invalidateQueries([queryKeys.user]);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useForgotPassword() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => forgotPassword(formData),
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useResetPassword() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => resetPassword(formData),
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useVerifyLoginOtp() {
  const { authenticate } = useContext(AuthContext);
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => verifyOtp(formData as any),
    onSuccess: (data) => {
      console.log("data from verification=>", data);
      setLoginToken(data.token);
      authenticate(data.token);
    },
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      // toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}
